import {Component, OnInit} from '@angular/core';
import {HeaderComponent} from '../../header/header.component';
import {FooterComponent} from '../../footer/footer.component';
import {CommonModule} from '@angular/common';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {environment} from '@env/environment';

// Angular Material
import {MatTableModule} from '@angular/material/table';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatCardModule} from '@angular/material/card';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {CdkDragDrop, DragDropModule, moveItemInArray} from '@angular/cdk/drag-drop';
import {DeleteDialogComponent} from '../../delete/delete-dialog.component';
import {ConstraintDialogComponent} from './constraint-dialog.component';

interface Constraint {
  id: string;
  scheduleId: number;
  definition: string;
  humanReadableDefinition: string;
  type: 'hard' | 'soft';
  order: number;
  weight?: number; // Weight for soft constraints
  active: boolean; // Whether constraint is active
  hasPendingChanges?: boolean; // Whether constraint has changes not yet in generated schedule
  addedInVersion?: number | null; // Version number when constraint was first added
  expanded?: boolean; // for UI expand/collapse
  editingWeight?: boolean; // for UI inline editing
  togglingActive?: boolean; // for UI loading state when toggling active status
}

interface Schedule {
  id: string;
  name: string;
  [key: string]: any;
}

@Component({
  selector: 'app-constraints',
  standalone: true,
  imports: [
    HeaderComponent,
    FooterComponent,
    CommonModule,
    FormsModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatTooltipModule,
    MatSlideToggleModule,
    DragDropModule,
  ],
  templateUrl: './constraints.component.html',
  styleUrls: ['./constraints.component.scss'],
})
export class ConstraintsComponent implements OnInit {
  constraints: Constraint[] = [];
  hardConstraints: Constraint[] = [];
  softConstraints: Constraint[] = [];
  schedule: Schedule | null = null;
  loading = false;
  loadingMessage = '';
  error: string | null = null;

  displayedColumns: string[] = [
    'spacer',
    'id',
    'active',
    'type',
    'weight',
    'humanReadableDefinition',
    'actions',
  ];
  displayedColumnsHard: string[] = [
    'spacer',
    'id',
    'active',
    'type',
    'weightSpacer',
    'humanReadableDefinition',
    'actions',
  ];
  isDragging = false;

  private apiBaseUrl = environment.apiBaseUrl;
  private organisationId: string = '';
  private scheduleId: string = '';

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    // Get selected organisationId from localStorage
    const storedId = localStorage.getItem('selectedOrganisationId');
    if (!storedId) {
      this.error = 'No organisation selected.';
      return;
    }
    this.organisationId = storedId;

    // Get schedule ID from route params
    this.route.paramMap.subscribe((params) => {
      const id = params.get('scheduleId');
      if (!id) {
        this.error = 'No schedule ID provided.';
        return;
      }
      this.scheduleId = id;
      this.fetchSchedule();
      this.fetchConstraints();
    });
  }

  fetchSchedule() {
    this.http
      .get<Schedule>(
        `${this.apiBaseUrl}/organisations/${this.organisationId}/schedules/${this.scheduleId}`
      )
      .subscribe({
        next: (data) => {
          this.schedule = data;
        },
        error: (err: HttpErrorResponse) => {
          this.error = err.message;
          this.snackBar.open('Failed to load schedule', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  fetchConstraints() {
    this.loading = true;
    this.http
      .get<Constraint[]>(
        `${this.apiBaseUrl}/schedules/${this.scheduleId}/constraints`
      )
      .subscribe({
        next: (data) => {
          this.constraints = data;
          // Separate hard and soft constraints
          this.hardConstraints = data.filter(c => c.type === 'hard');
          // Sort soft constraints by weight (highest first)
          this.softConstraints = data
            .filter(c => c.type === 'soft')
            .sort((a, b) => (b.weight || 100) - (a.weight || 100));
          this.loading = false;
        },
        error: (err: HttpErrorResponse) => {
          this.error = err.message;
          this.loading = false;
          this.snackBar.open('Failed to load constraints', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  openAddDialog() {
    const dialogRef = this.dialog.open(ConstraintDialogComponent, {
      width: '600px',
      data: {
        scheduleId: this.scheduleId,
        definition: '',
        humanReadableDefinition: '',
        type: 'hard',  // Default to hard constraint
        weight: 100,  // Default weight for soft constraints
        active: true,  // Default to active for new constraints
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (result.fromLibrary) {
          if (result.updateLibrary) {
            this.updateFromLibrary(result.librarySlug);
          } else {
            this.addFromLibrary(result);
          }
        } else {
          this.createConstraint(result);
        }
      }
    });
  }

  addFromLibrary(data: { librarySlug: string; weight?: number; active?: boolean }) {
    this.loading = true;
    this.loadingMessage = 'Adding constraint from library';

    this.http
      .post<Constraint>(
        `${this.apiBaseUrl}/schedules/${this.scheduleId}/constraints/from-library`,
        {
          librarySlug: data.librarySlug,
          weight: data.weight,
          active: data.active ?? true,
        }
      )
      .subscribe({
        next: () => {
          this.loading = false;
          this.loadingMessage = '';
          this.snackBar.open('Constraint added from library', 'Close', {
            duration: 2000,
          });
          this.fetchConstraints();
        },
        error: (err: HttpErrorResponse) => {
          this.loading = false;
          this.loadingMessage = '';

          let errorMsg = 'Failed to add constraint from library';
          if (err.error?.error) {
            errorMsg = err.error.error;
            if (err.error.details) {
              errorMsg += `: ${err.error.details}`;
            }
          }

          this.error = errorMsg;
          this.snackBar.open(errorMsg, 'Close', {
            duration: 5000,
          });
        },
      });
  }

  updateFromLibrary(librarySlug: string) {
    this.loading = true;
    this.loadingMessage = 'Updating constraint from library';

    this.http
      .put<Constraint>(
        `${this.apiBaseUrl}/schedules/${this.scheduleId}/constraints/from-library/${librarySlug}`,
        {}
      )
      .subscribe({
        next: (response: any) => {
          this.loading = false;
          this.loadingMessage = '';
          if (response.updated) {
            this.snackBar.open('Constraint updated from library', 'Close', {
              duration: 2000,
            });
          } else {
            this.snackBar.open('Constraint is already up to date', 'Close', {
              duration: 2000,
            });
          }
          this.fetchConstraints();
        },
        error: (err: HttpErrorResponse) => {
          this.loading = false;
          this.loadingMessage = '';

          let errorMsg = 'Failed to update constraint from library';
          if (err.error?.error) {
            errorMsg = err.error.error;
            if (err.error.details) {
              errorMsg += `: ${err.error.details}`;
            }
          }

          this.error = errorMsg;
          this.snackBar.open(errorMsg, 'Close', {
            duration: 5000,
          });
        },
      });
  }

  openEditDialog(constraint: Constraint) {
    const dialogRef = this.dialog.open(ConstraintDialogComponent, {
      width: '600px',
      data: { ...constraint },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.updateConstraint(constraint, result);
      }
    });
  }

  createConstraint(newConstraint: Partial<Constraint>) {
    this.loading = true;
    this.loadingMessage = 'Creating constraint and generating technical definition with AI';

    this.http
      .post<Constraint>(
        `${this.apiBaseUrl}/schedules/${this.scheduleId}/constraints`,
        newConstraint
      )
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.loadingMessage = '';
          this.snackBar.open('Constraint created successfully', 'Close', {
            duration: 2000,
          });
          this.fetchConstraints();
        },
        error: (err: HttpErrorResponse) => {
          this.loading = false;
          this.loadingMessage = '';

          // Format error message with details
          let errorMsg = 'Failed to create constraint';
          if (err.error) {
            if (typeof err.error === 'string') {
              errorMsg = err.error;
            } else if (err.error.error) {
              errorMsg = err.error.error;
              if (err.error.details) {
                errorMsg += `: ${err.error.details}`;
              }
            }
          }

          this.error = errorMsg;
          console.error('Full error object:', err.error);
          this.snackBar.open(errorMsg, 'Close', {
            duration: 5000,
          });
        },
      });
  }

  updateConstraint(constraint: Constraint, updatedData: Partial<Constraint>) {
    this.loading = true;
    this.loadingMessage = 'Updating constraint and regenerating technical definition with AI';

    this.http
      .put<Constraint>(
        `${this.apiBaseUrl}/schedules/${this.scheduleId}/constraints/${constraint.id}`,
        updatedData
      )
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.loadingMessage = '';
          this.snackBar.open('Constraint updated successfully', 'Close', {
            duration: 2000,
          });
          this.fetchConstraints();
        },
        error: (err: HttpErrorResponse) => {
          this.loading = false;
          this.loadingMessage = '';

          // Format error message with details
          let errorMsg = 'Failed to update constraint';
          if (err.error) {
            if (typeof err.error === 'string') {
              errorMsg = err.error;
            } else if (err.error.error) {
              errorMsg = err.error.error;
              if (err.error.details) {
                errorMsg += `: ${err.error.details}`;
              }
            }
          }

          this.error = errorMsg;
          console.error('Full error object:', err.error);
          this.snackBar.open(errorMsg, 'Close', {
            duration: 5000,
          });
        },
      });
  }

  deleteConstraint(constraint: Constraint) {
    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      width: '350px',
      data: {
        title: 'Delete Constraint',
        message: `Are you sure you want to delete this constraint?`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.http
          .delete(
            `${this.apiBaseUrl}/schedules/${this.scheduleId}/constraints/${constraint.id}`
          )
          .subscribe({
            next: () => {
              this.snackBar.open('Constraint deleted', 'Close', {
                duration: 2000,
              });
              this.fetchConstraints();
            },
            error: (err: HttpErrorResponse) => {
              this.error = err.error?.error || err.message;
              this.snackBar.open('Failed to delete constraint', 'Close', {
                duration: 3000,
              });
            },
          });
      }
    });
  }

  goBack() {
    this.router.navigate(['/schedules']);
  }

  // Hard constraints are not draggable, so no onDropHard method needed

  onDropSoft(event: CdkDragDrop<Constraint[]>) {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    // Create a copy of the array before reordering
    const reorderedSoftConstraints = [...this.softConstraints];
    moveItemInArray(reorderedSoftConstraints, event.previousIndex, event.currentIndex);

    // Update local arrays immediately for visual feedback
    this.softConstraints = reorderedSoftConstraints;

    // Update the backend
    this.updateConstraintOrder();
  }

  private updateConstraintOrder() {
    // Combine hard and soft constraints with hard constraints first
    const allConstraints = [...this.hardConstraints, ...this.softConstraints];
    const constraintIds = allConstraints.map(c => Number(c.id));

    // Update the main constraints array
    this.constraints = allConstraints;

    // Send reorder request to backend
    this.http
      .put(`${this.apiBaseUrl}/schedules/${this.scheduleId}/constraints/reorder`, {
        constraintIds
      })
      .subscribe({
        next: () => {
          this.snackBar.open('Constraints reordered successfully', 'Close', {
            duration: 2000,
          });
          // Don't refetch - keep the local state
        },
        error: (err: HttpErrorResponse) => {
          this.error = err.error?.error || err.message;
          this.snackBar.open('Failed to reorder constraints', 'Close', {
            duration: 3000,
          });
          // Reload to restore original order on error
          this.fetchConstraints();
        },
      });
  }

  onDragStarted() {
    this.isDragging = true;
  }

  onDragEnded() {
    this.isDragging = false;
  }

  // Toggle constraint active status
  toggleConstraintActive(constraint: Constraint) {
    const newActiveStatus = !constraint.active;

    // Set loading state
    constraint.togglingActive = true;
    this.snackBar.open(
      `${newActiveStatus ? 'Activating' : 'Deactivating'} constraint and deploying Lambda function...`,
      'Close',
      {
        duration: 5000,
      }
    );

    this.http
      .patch(`${this.apiBaseUrl}/schedules/${constraint.scheduleId}/constraints/${constraint.id}/active`, {
        active: newActiveStatus,
      })
      .subscribe({
        next: () => {
          constraint.active = newActiveStatus;
          constraint.togglingActive = false;
          this.snackBar.open(
            `Constraint ${newActiveStatus ? 'activated' : 'deactivated'} and Lambda function ready`,
            'Close',
            {
              duration: 2000,
              panelClass: ['success-snackbar'],
            }
          );
        },
        error: (err) => {
          console.error('Error toggling constraint active status:', err);
          constraint.togglingActive = false;
          this.snackBar.open('Failed to update constraint status', 'Close', {
            duration: 3000,
            panelClass: ['error-snackbar'],
          });
        },
      });
  }

  // Regenerate a single constraint
  regenerateConstraint(constraint: Constraint) {
    const confirmDialog = this.dialog.open(DeleteDialogComponent, {
      width: '400px',
      data: {
        title: 'Regenerate Constraint',
        message: `Are you sure you want to regenerate the Python code for constraint "${constraint.humanReadableDefinition}"? This will overwrite the existing constraint file.`,
        confirmText: 'Regenerate',
        cancelText: 'Cancel',
      },
    });

    confirmDialog.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.loading = true;
        this.snackBar.open('Regenerating constraint...', 'Close', {
          duration: 5000,
        });

        this.http
          .post(
            `${this.apiBaseUrl}/schedules/${constraint.scheduleId}/constraints/${constraint.id}/regenerate`,
            {}
          )
          .subscribe({
            next: (response: any) => {
              this.loading = false;
              this.snackBar.open(
                response.message || 'Constraint regenerated successfully',
                'Close',
                {
                  duration: 3000,
                  panelClass: ['success-snackbar'],
                }
              );
              // Optionally refresh the constraints list
              this.fetchConstraints();
            },
            error: (err) => {
              this.loading = false;
              console.error('Error regenerating constraint:', err);
              this.snackBar.open(
                err.error?.error || 'Failed to regenerate constraint',
                'Close',
                {
                  duration: 3000,
                  panelClass: ['error-snackbar'],
                }
              );
            },
          });
      }
    });
  }
}
